import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Science,
  RocketLaunch,
  Check,
  Star,
  Warning as WarningIcon,
  VerifiedUser,
  Lightbulb,
  EditNote,
  TrendingUp,
  Lock,
  ExpandMore,
  HelpOutline,
} from '@mui/icons-material';

/* ------------------------------------------------------------------ */
/*  Brand palette                                                      */
/* ------------------------------------------------------------------ */
const BRAND = {
  amber: '#E8A045',
  amberHover: '#D08E35',
  teal: '#3A8C8C',
  tealLight: '#6BB8B8',
  cream: '#FDF6EC',
  sand: '#F0E4CE',
  brown: '#4A3728',
  brownLt: '#7A5F4E',
  white: '#FFFFFF',
};

const FONT = {
  headline: '"Fraunces", serif',
  body: '"DM Sans", sans-serif',
};

/* ------------------------------------------------------------------ */
/*  Logo SVG component                                                 */
/* ------------------------------------------------------------------ */
const QuantatoLogo: React.FC = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <circle cx="22" cy="22" r="21" fill={BRAND.teal} fillOpacity={0.12} />
    <path
      d="M22 7 C14.5 7 10 13.5 10 19.5 C10 26.5 15.5 30.5 19.5 32.5 L19.5 36 C19.5 37.4 20.6 38.5 22 38.5 C23.4 38.5 24.5 37.4 24.5 36 L24.5 32.5 C28.5 30.5 34 26.5 34 19.5 C34 13.5 29.5 7 22 7Z"
      fill={BRAND.teal}
    />
    <ellipse cx="19" cy="18" rx="3.5" ry="4.5" fill="#6BB8B8" opacity={0.7} transform="rotate(-15 19 18)" />
    <circle cx="30" cy="11" r="4" fill={BRAND.amber} />
    <circle cx="30" cy="11" r="2" fill="#F5C97A" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Pricing tiers — mirrors the in-app token economy (see Pricing.tsx) */
/*  Keep prices/tokens/limits in sync with src/pages/Pricing.tsx.      */
/* ------------------------------------------------------------------ */
const TIERS = [
  {
    name: 'Free',
    key: 'free',
    price: '$0',
    period: 'forever',
    tokens: '500 tokens',
    cta: 'Start Free',
    description: 'Try it out. No credit card.',
    limits: ['1 live + 2 paper strategies', '500 tokens (one-time)'],
    features: ['AI Strategy Builder', 'Basic analytics', 'Paper + live trading'],
  },
  {
    name: 'Starter',
    key: 'starter',
    price: '$29',
    period: '/mo',
    tokens: '2,500 tokens/mo',
    cta: 'Start Starter',
    description: 'For getting going.',
    limits: ['2 live + 3 paper strategies', '2,500 tokens / month'],
    features: ['AI recommendations', 'Rule refinement', 'Email support'],
  },
  {
    name: 'Pro',
    key: 'pro',
    price: '$79',
    period: '/mo',
    highlighted: true,
    tokens: '6,000 tokens/mo',
    cta: 'Start Pro',
    description: 'For active traders.',
    limits: ['5 live + 10 paper strategies', '6,000 tokens / month'],
    features: ['Everything in Starter', 'Portfolio optimization', 'Advanced analytics', 'Priority support'],
  },
  {
    name: 'The Boss',
    key: 'the_boss',
    price: '$199',
    period: '/mo',
    tokens: '12,000 tokens/mo',
    cta: 'Go Boss',
    description: 'For power users.',
    limits: ['15 live strategies', 'Unlimited paper + backtests'],
    features: ['Everything in Pro', 'Help chat', 'Dedicated support'],
  },
  {
    name: 'AI Trader',
    key: 'ai_trader',
    price: '$299',
    period: '/mo',
    premium: true,
    tokens: '15,000 tokens/mo',
    cta: 'Go AI Trader',
    description: 'Full AI power.',
    limits: ['25 live + 50 paper strategies', '15,000 tokens / month'],
    features: ['Everything in The Boss', 'AI evaluates every trade', 'AI-powered trading rules'],
  },
];

/* ------------------------------------------------------------------ */
/*  How-it-works steps                                                 */
/* ------------------------------------------------------------------ */
const STEPS = [
  {
    icon: <Lightbulb sx={{ fontSize: 44, color: BRAND.amber }} />,
    step: '1',
    title: 'Describe your idea',
    description: 'Type what you think will happen — in plain English. AI turns it into a real trading strategy.',
  },
  {
    icon: <Science sx={{ fontSize: 44, color: BRAND.teal }} />,
    step: '2',
    title: 'Test it first',
    description: 'Run it against real market data. See how it would have performed — before risking a cent.',
  },
  {
    icon: <RocketLaunch sx={{ fontSize: 44, color: BRAND.amber }} />,
    step: '3',
    title: 'Go live when ready',
    description: 'Flip the switch and your strategy trades automatically on Kalshi. Safety limits built in. You stay in control.',
  },
];

/* ------------------------------------------------------------------ */
/*  Features                                                           */
/* ------------------------------------------------------------------ */
const FEATURES = [
  {
    icon: <EditNote sx={{ fontSize: 48, color: BRAND.teal }} />,
    title: 'AI Strategy Builder',
    description: 'Say what you think will happen. AI builds the trading rules — when to buy, when to sell, how much to risk.',
  },
  {
    icon: <Science sx={{ fontSize: 48, color: BRAND.amber }} />,
    title: 'Test Before You Trade',
    description: 'We simulate your strategy 1,000 times against real data. You see the range of outcomes, not just one guess.',
  },
  {
    icon: <TrendingUp sx={{ fontSize: 48, color: BRAND.teal }} />,
    title: 'Autopilot Trading',
    description: 'Your strategies run 24/7. Stop-losses, position limits, and safety shutoffs protect your capital automatically.',
  },
];

/* ------------------------------------------------------------------ */
/*  Section wrapper                                                    */
/* ------------------------------------------------------------------ */
const Section: React.FC<{
  children: React.ReactNode;
  bg?: string;
  id?: string;
}> = ({ children, bg, id }) => (
  <Box id={id} sx={{ py: { xs: 8, md: 10 }, backgroundColor: bg || 'transparent' }}>
    <Container maxWidth="lg">{children}</Container>
  </Box>
);

/* ================================================================== */
/*  LandingPage                                                        */
/* ================================================================== */
interface FeaturedListing {
  id: number;
  public_name: string;
  public_description: string | null;
  purchase_price_tokens: number;
  total_purchases: number;
  creator_display_name: string | null;
  monte_carlo_snapshot: any;
}

const getAppUrl = (path: string): string | null => {
  const host = window.location.hostname;
  if (host === 'quantato.ai' || host === 'www.quantato.ai') {
    return `https://app.quantato.ai${path}`;
  }
  return null;
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState<FeaturedListing[]>([]);

  const goTo = (path: string) => {
    const absolute = getAppUrl(path);
    if (absolute) {
      window.location.href = absolute;
    } else {
      navigate(path);
    }
  };

  useEffect(() => {
    fetch('/api/v1/marketplace/featured')
      .then(res => res.ok ? res.json() : [])
      .then(data => setFeatured(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  return (
    <Box sx={{ backgroundColor: BRAND.cream, minHeight: '100vh', color: BRAND.brown, fontFamily: FONT.body }} className="responsive-container">
      {/* ──────────────────── Top Nav ──────────────────── */}
      <Box
        component="nav"
        role="navigation"
        aria-label="Main navigation"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          backgroundColor: 'rgba(253,246,236,0.95)',
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${BRAND.sand}`,
          py: 1,
          px: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuantatoLogo />
            <Typography
              variant="h6"
              component="span"
              sx={{ fontFamily: FONT.headline, fontWeight: 700, color: BRAND.brown, fontSize: '1.2rem' }}
            >
              Quantato
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {[
              { label: 'How It Works', href: '#how-it-works' },
              { label: 'Features', href: '#features' },
              { label: 'Pricing', href: '#pricing' },
            ].map((link) => (
              <Button
                key={link.label}
                size="small"
                href={link.href}
                sx={{
                  color: BRAND.brownLt,
                  textTransform: 'none',
                  fontFamily: FONT.body,
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  minWidth: 'auto',
                  px: 1.5,
                  '&:hover': { color: BRAND.teal, backgroundColor: 'transparent' },
                }}
              >
                {link.label}
              </Button>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={() => goTo('/login')}
              sx={{ color: BRAND.brown, textTransform: 'none', fontFamily: FONT.body, fontWeight: 600, '&:hover': { color: BRAND.teal } }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => goTo('/login?tab=register')}
              sx={{
                backgroundColor: BRAND.amber,
                color: BRAND.white,
                textTransform: 'none',
                fontFamily: FONT.body,
                fontWeight: 600,
                borderRadius: '6px',
                px: 2,
                '&:hover': { backgroundColor: BRAND.amberHover },
              }}
            >
              Get Started
            </Button>
          </Box>
        </Container>
      </Box>

      <Box component="main" id="main-content" role="main" aria-label="Main content">

      {/* ──────────────────── Hero ──────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND.cream} 0%, #FFF9F0 40%, ${BRAND.cream} 100%)`,
          pt: { xs: 8, md: 14 },
          pb: { xs: 10, md: 14 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(232,160,69,0.08) 0%, transparent 70%)`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontFamily: FONT.headline,
              fontWeight: 800,
              mb: 3,
              color: BRAND.brown,
              fontSize: { xs: '2.2rem', md: '3.2rem' },
              lineHeight: 1.2,
            }}
          >
            You have the ideas.{'\n'}We do the math.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 5,
              color: BRAND.brownLt,
              maxWidth: 540,
              mx: 'auto',
              lineHeight: 1.7,
              fontWeight: 400,
              fontFamily: FONT.body,
              fontSize: { xs: '1rem', md: '1.15rem' },
            }}
          >
            Describe a prediction in plain English. We turn it into a trading strategy,
            test it against real data, and run it on Kalshi — automatically.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => goTo('/login?tab=register')}
              sx={{
                px: 5,
                height: 48,
                fontWeight: 700,
                fontSize: '1.05rem',
                fontFamily: FONT.body,
                backgroundColor: BRAND.amber,
                color: BRAND.white,
                '&:hover': { backgroundColor: BRAND.amberHover },
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(232,160,69,0.3)',
              }}
            >
              Try It Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => goTo('/login')}
              sx={{
                px: 5,
                height: 48,
                fontWeight: 600,
                fontSize: '1.05rem',
                fontFamily: FONT.body,
                borderColor: BRAND.sand,
                color: BRAND.brown,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': { borderColor: BRAND.teal, backgroundColor: 'rgba(58,140,140,0.05)' },
              }}
            >
              Sign In
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ──────────────────── How It Works ──────────────────── */}
      <Section bg={BRAND.white} id="how-it-works">
        <Typography
          variant="h4"
          component="h2"
          sx={{ fontFamily: FONT.headline, fontWeight: 700, mb: 1, textAlign: 'center', color: BRAND.brown }}
        >
          Three steps. That's it.
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: BRAND.brownLt, mb: 6, textAlign: 'center', fontSize: '1.05rem', fontFamily: FONT.body }}
        >
          From idea to live trading in under two minutes.
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {STEPS.map((s, idx) => (
            <Grid item xs={12} md={4} key={s.step}>
              <Box sx={{ textAlign: 'center', position: 'relative' }}>
                {idx < STEPS.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      top: 40,
                      right: -32,
                      width: 64,
                      borderTop: `2px dashed ${BRAND.sand}`,
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, rgba(58,140,140,0.1), rgba(232,160,69,0.08))`,
                    border: `1px solid ${BRAND.sand}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2.5,
                  }}
                >
                  {s.icon}
                </Box>
                <Typography
                  variant="overline"
                  sx={{ color: BRAND.brownLt, fontWeight: 700, letterSpacing: 2, fontFamily: FONT.body }}
                >
                  Step {s.step}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontFamily: FONT.headline, fontWeight: 600, mb: 1, mt: 0.5, color: BRAND.brown }}
                >
                  {s.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: BRAND.brownLt, lineHeight: 1.8, maxWidth: 320, mx: 'auto', fontFamily: FONT.body }}
                >
                  {s.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ──────────────────── Features ──────────────────── */}
      <Section bg={BRAND.cream} id="features">
        <Typography
          variant="h4"
          component="h2"
          sx={{ fontFamily: FONT.headline, fontWeight: 700, mb: 6, textAlign: 'center', color: BRAND.brown }}
        >
          What You Get
        </Typography>
        <Grid container spacing={4}>
          {FEATURES.map((f) => (
            <Grid item xs={12} md={4} key={f.title}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: BRAND.white,
                  border: `1px solid ${BRAND.sand}`,
                  borderRadius: '16px',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 12px rgba(74,55,40,0.06)',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(74,55,40,0.1)',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 2.5 }}>{f.icon}</Box>
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: FONT.headline, fontWeight: 600, mb: 1.5, color: BRAND.brown }}
                  >
                    {f.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BRAND.brownLt, lineHeight: 1.8, fontFamily: FONT.body }}>
                    {f.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* ──────────────────── Security + Trust ──────────────────── */}
      <Box
        sx={{
          py: 5,
          background: `linear-gradient(90deg, rgba(58,140,140,0.06) 0%, rgba(232,160,69,0.06) 100%)`,
          borderTop: `1px solid ${BRAND.sand}`,
          borderBottom: `1px solid ${BRAND.sand}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {[
              {
                icon: <Lock sx={{ fontSize: 24, color: BRAND.teal, mt: 0.3 }} />,
                title: 'Encrypted at rest',
                desc: 'Your API keys are encrypted before storage. Even if our database were compromised, your keys stay safe.',
              },
              {
                icon: <VerifiedUser sx={{ fontSize: 24, color: BRAND.teal, mt: 0.3 }} />,
                title: 'No hidden fees',
                desc: 'You pay your subscription. Kalshi handles exchange fees. We never take a cut of your trades.',
              },
              {
                icon: <VerifiedUser sx={{ fontSize: 24, color: BRAND.teal, mt: 0.3 }} />,
                title: 'Your keys, your control',
                desc: 'Trades go through your own Kalshi account. We can\'t withdraw funds. Revoke access anytime.',
              },
            ].map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {item.icon}
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5, color: BRAND.brown, fontFamily: FONT.body }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND.brownLt, lineHeight: 1.7, fontFamily: FONT.body }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ──────────────────── Pricing ──────────────────── */}
      <Section bg={BRAND.white} id="pricing">
        <Typography
          variant="h4"
          component="h2"
          sx={{ fontFamily: FONT.headline, fontWeight: 700, mb: 1, textAlign: 'center', color: BRAND.brown }}
        >
          Simple Pricing
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: BRAND.brownLt, mb: 6, textAlign: 'center', fontSize: '1.05rem', fontFamily: FONT.body }}
        >
          Every plan includes monthly tokens — spend them on backtests, AI builds, and recommendations. Upgrade when you need more.
        </Typography>
        <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 1200, mx: 'auto' }}>
          {TIERS.map((tier) => {
            const isPremium = !!(tier as any).premium;
            const isHighlighted = !!(tier as any).highlighted;
            return (
              <Grid item xs={12} sm={6} md={2.4} key={tier.key}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                    border: isPremium
                      ? `2px solid ${BRAND.amber}`
                      : isHighlighted
                      ? `2px solid ${BRAND.teal}`
                      : `1px solid ${BRAND.sand}`,
                    backgroundColor: BRAND.white,
                    borderRadius: '16px',
                    position: 'relative',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0 2px 12px rgba(74,55,40,0.06)',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(74,55,40,0.1)' },
                  }}
                >
                  {isHighlighted && (
                    <Chip
                      label="Most Popular"
                      icon={<Star sx={{ fontSize: 16 }} />}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -14,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: BRAND.teal,
                        color: BRAND.white,
                        fontWeight: 600,
                        fontFamily: FONT.body,
                        zIndex: 1,
                        height: 28,
                      }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: isHighlighted ? 4 : 3, px: 3 }}>
                    <Typography variant="h6" sx={{ fontFamily: FONT.headline, fontWeight: 600, mb: 0.5, color: BRAND.brown }}>
                      {tier.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: BRAND.brown, fontFamily: FONT.headline }}>
                        {tier.price}
                      </Typography>
                      <Typography variant="body2" sx={{ ml: 0.5, color: BRAND.brownLt, fontFamily: FONT.body }}>
                        {tier.period}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: BRAND.brownLt, mb: 1.5, fontFamily: FONT.body }}>
                      {tier.description}
                    </Typography>

                    <Typography variant="body2" sx={{ color: BRAND.teal, fontWeight: 700, mb: 2, fontFamily: FONT.body }}>
                      {tier.tokens}
                    </Typography>

                    <Divider sx={{ mb: 2, borderColor: BRAND.sand }} />

                    {/* Limits */}
                    {tier.limits.map((limit, i) => (
                      <Typography key={i} variant="body2" sx={{ color: BRAND.brown, fontWeight: 500, fontFamily: FONT.body, mb: 0.5 }}>
                        {limit}
                      </Typography>
                    ))}

                    <Divider sx={{ my: 2, borderColor: BRAND.sand }} />

                    {/* Features */}
                    <List dense disablePadding sx={{ flexGrow: 1 }}>
                      {tier.features.map((feature, i) => (
                        <ListItem key={i} disableGutters sx={{ py: 0.2 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <Check sx={{ fontSize: 16, color: BRAND.teal }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{ fontSize: 13, color: BRAND.brownLt, fontFamily: FONT.body }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Button
                        variant={(isHighlighted || isPremium) ? 'contained' : 'outlined'}
                        fullWidth
                        onClick={() => goTo('/login?tab=register')}
                        sx={{
                          borderRadius: '8px',
                          fontWeight: 600,
                          fontFamily: FONT.body,
                          height: 44,
                          textTransform: 'none',
                          ...((isHighlighted || isPremium)
                            ? {
                                backgroundColor: isHighlighted ? BRAND.teal : BRAND.amber,
                                color: BRAND.white,
                                '&:hover': { backgroundColor: isHighlighted ? '#2A6666' : BRAND.amberHover },
                              }
                            : {
                                borderColor: BRAND.sand,
                                color: BRAND.brown,
                                '&:hover': { borderColor: BRAND.teal, backgroundColor: 'rgba(58,140,140,0.04)' },
                              }),
                        }}
                      >
                        {tier.cta}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Section>

      {/* ──────────────────── Marketplace ──────────────────── */}
      {featured.length > 0 && (
        <Section bg={BRAND.cream} id="marketplace">
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontFamily: FONT.headline, fontWeight: 700, mb: 1, textAlign: 'center', color: BRAND.brown }}
          >
            Strategy Marketplace
          </Typography>
          <Typography variant="body1" sx={{ color: BRAND.brownLt, mb: 5, textAlign: 'center', fontFamily: FONT.body }}>
            Don't want to build your own? Buy a tested strategy from the community.
          </Typography>

          <Grid container spacing={3} sx={{ maxWidth: 1000, mx: 'auto', mb: 4 }}>
            {featured.map((listing) => {
              const ev = listing.monte_carlo_snapshot?.summary?.mean_return;
              return (
                <Grid item xs={12} sm={6} md={4} key={listing.id}>
                  <Card sx={{ p: 2.5, height: '100%', border: `1px solid ${BRAND.sand}`, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontFamily: FONT.headline, fontWeight: 600, lineHeight: 1.3 }}>
                        {listing.public_name}
                      </Typography>
                      {ev != null && (
                        <Chip
                          label={`${(ev * 100).toFixed(0)}% EV`}
                          size="small"
                          sx={{ fontWeight: 700, fontSize: 11, backgroundColor: 'rgba(39,174,96,0.12)', color: '#27AE60', flexShrink: 0, ml: 1 }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: BRAND.brownLt, mb: 1.5, flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {listing.public_description || 'No description'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: BRAND.brownLt }}>
                      by {listing.creator_display_name || 'Anonymous'}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => goTo('/login')}
              sx={{ color: BRAND.teal, borderColor: BRAND.teal, textTransform: 'none', fontFamily: FONT.body, fontWeight: 600, '&:hover': { borderColor: BRAND.teal, backgroundColor: 'rgba(58,140,140,0.08)' } }}
            >
              Browse all strategies
            </Button>
          </Box>
        </Section>
      )}

      {/* ──────────────────── CTA ──────────────────── */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          textAlign: 'center',
          background: `linear-gradient(135deg, rgba(58,140,140,0.05) 0%, rgba(232,160,69,0.05) 100%)`,
        }}
      >
        <Container maxWidth="sm">
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontFamily: FONT.headline, fontWeight: 700, mb: 1.5, color: BRAND.brown }}
          >
            Ready to try your first idea?
          </Typography>
          <Typography variant="body1" sx={{ color: BRAND.brownLt, mb: 4, fontFamily: FONT.body }}>
            Free to start. No credit card required.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => goTo('/login?tab=register')}
            sx={{
              px: 6,
              height: 48,
              fontWeight: 700,
              fontSize: '1.05rem',
              fontFamily: FONT.body,
              backgroundColor: BRAND.amber,
              color: BRAND.white,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(232,160,69,0.3)',
              '&:hover': { backgroundColor: BRAND.amberHover },
            }}
          >
            Get Started Free
          </Button>
        </Container>
      </Box>

      {/* ──────────────────── FAQ ──────────────────── */}
      <Section bg={BRAND.cream} id="faq">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
          <HelpOutline sx={{ color: BRAND.teal, fontSize: 32 }} />
          <Typography variant="h4" component="h2" sx={{ fontFamily: FONT.headline, fontWeight: 700, color: BRAND.brown }}>
            FAQ
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {[
            {
              q: 'How does backtesting work?',
              a: 'We run your strategy 1,000 times by randomly sampling from real market outcomes. Instead of one prediction, you get a range of results: best case, worst case, and most likely.',
            },
            {
              q: 'Do I need to know anything about trading?',
              a: 'No. Describe your idea in plain English — "I think oil will go up this summer" — and AI builds the full strategy. You can tweak the details if you want, or just run it as-is.',
            },
            {
              q: 'Is my money safe?',
              a: 'Your trades go through your own Kalshi account. We can\'t withdraw funds or move money. API keys are encrypted at rest. You can revoke access from Kalshi anytime.',
            },
            {
              q: 'What does paper trading mean?',
              a: 'Paper trading is simulated trading — no real money involved. Your strategy runs against live markets but only tracks what would have happened. It\'s the safest way to test an idea.',
            },
            {
              q: 'Can I trust the backtest results?',
              a: 'Backtests show what could have happened, not what will happen. Markets change. Past performance doesn\'t guarantee future results. Use them as one input in your decision, not a guarantee.',
            },
          ].map((item, idx) => (
            <Accordion
              key={idx}
              sx={{
                mb: 1.5,
                backgroundColor: BRAND.white,
                border: `1px solid ${BRAND.sand}`,
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                boxShadow: '0 1px 4px rgba(74,55,40,0.04)',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: BRAND.teal }} />}>
                <Typography sx={{ fontWeight: 600, color: BRAND.brown, fontFamily: FONT.body }}>
                  {item.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ color: BRAND.brownLt, lineHeight: 1.8, fontFamily: FONT.body }}>
                  {item.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Section>

      {/* ──────────────────── Risk Disclosure ──────────────────── */}
      <Section bg={BRAND.white} id="risk-disclosure">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 3 }}>
          <WarningIcon sx={{ color: BRAND.amber, fontSize: 32 }} />
          <Typography variant="h4" component="h2" sx={{ fontFamily: FONT.headline, fontWeight: 700, color: BRAND.brown }}>
            Risk Disclosure
          </Typography>
        </Box>
        <Box
          sx={{
            maxWidth: 900,
            mx: 'auto',
            backgroundColor: BRAND.cream,
            border: `1px solid ${BRAND.sand}`,
            borderRadius: '12px',
            p: { xs: 3, md: 5 },
          }}
        >
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: BRAND.brown, fontFamily: FONT.body }}>
            Quantato is an independent platform that connects to Kalshi via their public API. We are not affiliated with or endorsed by Kalshi Inc.
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 0, '& li': { mb: 1.5, color: BRAND.brown, lineHeight: 1.7, fontFamily: FONT.body } }}>
            <li><strong>Trading risk:</strong> You may lose some or all of your invested capital. Past performance and backtests don't guarantee future results.</li>
            <li><strong>Not financial advice:</strong> Quantato doesn't provide financial or investment advice. All decisions are yours.</li>
            <li><strong>Software as-is:</strong> We don't guarantee accuracy, uptime, or trade execution. Automated systems can malfunction.</li>
            <li><strong>Your responsibility:</strong> You're responsible for monitoring your account, securing your API keys, and complying with applicable laws.</li>
          </Box>
        </Box>
      </Section>

      </Box>{/* end main */}

      {/* ──────────────────── Footer ──────────────────── */}
      <Box
        component="footer"
        role="contentinfo"
        sx={{ borderTop: `1px solid ${BRAND.sand}`, py: 4, textAlign: 'center', backgroundColor: BRAND.cream }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', mb: 2 }}>
            {[
              { label: 'FAQ', action: () => goTo('/faq') },
              { label: 'About', action: () => goTo('/about') },
              { label: 'Terms', action: () => goTo('/terms') },
              { label: 'Privacy', action: () => goTo('/privacy') },
              { label: 'Compliance', action: () => goTo('/compliance') },
            ].map((link, i) => (
              <React.Fragment key={link.label}>
                {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: BRAND.sand }} />}
                <Button
                  size="small"
                  onClick={link.action}
                  sx={{ color: BRAND.brownLt, textTransform: 'none', fontFamily: FONT.body, '&:hover': { color: BRAND.teal } }}
                >
                  {link.label}
                </Button>
              </React.Fragment>
            ))}
          </Box>
          <Typography variant="body2" sx={{ color: BRAND.brownLt, fontFamily: FONT.body, opacity: 0.7 }}>
            &copy; {new Date().getFullYear()} Quantato, Inc. All rights reserved.
          </Typography>
          <Typography variant="caption" sx={{ color: BRAND.brownLt, fontFamily: FONT.body, display: 'block', mt: 0.5, opacity: 0.5 }}>
            Not affiliated with Kalshi Inc.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
